import _ from 'lodash';

import { checkFormState, normalizeOptionValues } from '@/utils/core/business-logic';
import {
  getEnumValue,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';

/** Enum keys excluded from onboarding (not registrable). */
const ONBOARDING_EXCLUDED_ENUM_KEYS = new Set(['None']);

/** Actors that create a row in channel entity tables */
const ENTITY_CHANNEL_ACTOR_ENUM_KEYS = Object.freeze([
  'Operator',
  'Distributor',
  'Partner',
  'Reseller',
  'ReferralAgent',
]);

const toActorKindApiValue = (enumKey) => _.kebabCase(enumKey);

const buildActorKindsFromEnum = (enumObject) =>
  Object.freeze(
    Object.fromEntries(
      Object.keys(enumObject)
        .filter((key) => !ONBOARDING_EXCLUDED_ENUM_KEYS.has(key))
        .map((key) => [_.snakeCase(key).toUpperCase(), toActorKindApiValue(key)]),
    ),
  );

/** API string values for onboarding, derived from CoreEnums.ChannelActorKind. */
export const ACTOR_KINDS = buildActorKindsFromEnum(CoreEnums.ChannelActorKind);

const VALID_ACTOR_KINDS = new Set(Object.values(ACTOR_KINDS));

const ENTITY_ACTOR_KIND_API_VALUES = new Set(
  ENTITY_CHANNEL_ACTOR_ENUM_KEYS.map(toActorKindApiValue),
);

/** Maps `?type=` query param to canonical kebab-case `actorKind`. */
export const resolveActorKindFromQuery = (typeParam) => {
  if (!typeParam) return undefined;
  if (VALID_ACTOR_KINDS.has(typeParam)) return typeParam;
  const normalized = _.kebabCase(typeParam);
  return VALID_ACTOR_KINDS.has(normalized) ? normalized : undefined;
};

export const isEntityActorKind = (actorKind) => ENTITY_ACTOR_KIND_API_VALUES.has(actorKind);

/**
 * Commercial parent rank (lower = higher in the tree).
 * Do not use ChannelActorKind numeric values: Customer is 6 and ReferralAgent is 7.
 */
export const CHANNEL_ACTOR_COMMERCIAL_RANK = Object.freeze({
  [ACTOR_KINDS.AUTOMY_INTERNAL]: 0,
  [ACTOR_KINDS.OPERATOR]: 1,
  [ACTOR_KINDS.DISTRIBUTOR]: 2,
  [ACTOR_KINDS.PARTNER]: 3,
  [ACTOR_KINDS.RESELLER]: 4,
  [ACTOR_KINDS.REFERRAL_AGENT]: 5,
  [ACTOR_KINDS.CUSTOMER]: 6,
});

const PARENT_SELECTOR_CHILD_ACTOR_KINDS = new Set([
  ACTOR_KINDS.DISTRIBUTOR,
  ACTOR_KINDS.PARTNER,
  ACTOR_KINDS.RESELLER,
  ACTOR_KINDS.REFERRAL_AGENT,
]);

export const shouldShowParentActorSelector = (actorKind) =>
  PARENT_SELECTOR_CHILD_ACTOR_KINDS.has(actorKind);

export const getSuperiorParentActorKinds = (childActorKind) => {
  const childRank = CHANNEL_ACTOR_COMMERCIAL_RANK[childActorKind];
  if (childRank == null) return [];
  return Object.keys(CHANNEL_ACTOR_COMMERCIAL_RANK).filter(
    (kind) => CHANNEL_ACTOR_COMMERCIAL_RANK[kind] < childRank,
  );
};

const PENDING_APPROVAL_REGISTRATION_STATUS = 'pending-approval';
const REJECTED_REGISTRATION_STATUS = 'rejected';

const unwrapRegistrationStatus = (registerResult) => {
  const raw = registerResult?.registrationStatus;
  if (raw == null || raw === '') return '';
  const value = typeof raw === 'object' ? raw.value ?? raw.id ?? raw.key : raw;
  return String(value);
};

export const normalizeRegistrationStatus = (registerResult) =>
  _.kebabCase(unwrapRegistrationStatus(registerResult));

/** True when POST /register left the company waiting for Automy review. */
export const isPendingApprovalRegistration = (registerResult) =>
  normalizeRegistrationStatus(registerResult) === PENDING_APPROVAL_REGISTRATION_STATUS;

/** True when Automy rejected the registration. Company is not operational. */
export const isRejectedRegistration = (registerResult) =>
  normalizeRegistrationStatus(registerResult) === REJECTED_REGISTRATION_STATUS;

/** Pending or rejected: no operational summary, invite, or success toast. */
export const isNonOperationalRegistration = (registerResult) =>
  isPendingApprovalRegistration(registerResult) || isRejectedRegistration(registerResult);

/** All onboarding actor kinds may send top-level `billingSetup`. */
export const ACTOR_KINDS_WITH_BILLING_SETUP = new Set(Object.values(ACTOR_KINDS));

export const normalizeAllowedBillingModes = (raw) => {
  let list = raw;
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];
  return list
    .map((item) => parseOptionalNumericEnumFromApi(CoreEnums.ChannelBillingMode, item))
    .filter(
      (value) => value !== undefined && value !== CoreEnums.ChannelBillingMode.DistributorCredit,
    );
};

export const resolveSelectedPlan = (automyPlanId, planApiData) => {
  if (automyPlanId == null || automyPlanId === '') return null;
  const planId =
    typeof automyPlanId === 'object' ? automyPlanId.value ?? automyPlanId.id : automyPlanId;
  if (planId == null || planId === '') return null;

  const plans = planApiData?.data;
  if (!Array.isArray(plans)) return null;
  const idStr = String(planId);
  return (
    plans.find((item) => String(item?.value ?? item?.id) === idStr) ||
    (typeof automyPlanId === 'object' ? automyPlanId : null)
  );
};

/**
 * ISO2 of the selected country.
 *
 * Prefer `countryId.code` from the form value (Ace Select persists it when
 * `countryFlag` is on). Fall back to the loaded options: those disappear when
 * the company step unmounts and resets `apiData`, which is why the summary
 * cannot rely on that cache alone.
 */
export const resolveSelectedCountryCode = (countryId, countryApiData) => {
  if (countryId != null && typeof countryId === 'object' && countryId.code) {
    return countryId.code;
  }

  const id = typeof countryId === 'object' ? countryId?.value ?? countryId?.id : countryId;
  if (id == null || id === '') return null;

  const countries = countryApiData?.data;
  if (!Array.isArray(countries)) return null;

  const idStr = String(id);
  const match = countries.find((item) => String(item?.value ?? item?.id) === idStr);
  return match?.code ?? null;
};

/** Backend ChannelOnboardingStep enum values (kebab-case). */
export const ONBOARDING_STEP_API_VALUES = Object.freeze({
  'registration-type': 'ENTITY_TYPE',
  'automy-plan': 'PLAN',
  company: 'COMPANY',
  'channel-actor': 'ACTOR',
  'commercial-agreement': 'AGREEMENT',
  billing: 'BILLING',
  summary: 'SUMMARY',
});

export const WIZARD_STEPS = Object.freeze({
  ENTITY_TYPE: 0,
  PLAN: 1,
  COMPANY: 2,
  ACTOR: 3,
  AGREEMENT: 4,
  BILLING: 5,
  SUMMARY: 6,
});

const DEFAULT_ENTITY_REQUIRED_STEPS = Object.freeze([
  'registration-type',
  'automy-plan',
  'company',
  'channel-actor',
  'commercial-agreement',
  'billing',
  'summary',
]);

const DEFAULT_NON_ENTITY_REQUIRED_STEPS = Object.freeze([
  'registration-type',
  'automy-plan',
  'company',
  'billing',
  'summary',
]);

export const getDefaultRequiredStepsForActorKind = (actorKind) =>
  isEntityActorKind(actorKind) ? DEFAULT_ENTITY_REQUIRED_STEPS : DEFAULT_NON_ENTITY_REQUIRED_STEPS;

const mapRequiredStepsToWizardSteps = (requiredSteps = []) => {
  const mapped = requiredSteps
    .map((step) => {
      const stepKey = ONBOARDING_STEP_API_VALUES[step];
      return stepKey ? WIZARD_STEPS[stepKey] : null;
    })
    .filter((step) => step !== null);

  return mapped.length ? mapped : null;
};

export const getWizardStepsForActorKind = (actorKind, registrationOptions = []) => {
  if (!actorKind) return [WIZARD_STEPS.ENTITY_TYPE];

  const option = registrationOptions.find((entry) => entry.actorKind === actorKind);
  const requiredSteps = [...(option?.requiredSteps || [])];

  if (
    option?.requiresCommercialAgreement === true &&
    !requiredSteps.includes('commercial-agreement')
  ) {
    const billingIdx = requiredSteps.indexOf('billing');
    const summaryIdx = requiredSteps.indexOf('summary');
    const insertAt = billingIdx >= 0 ? billingIdx : summaryIdx;
    if (insertAt >= 0) {
      requiredSteps.splice(insertAt, 0, 'commercial-agreement');
    } else {
      requiredSteps.push('commercial-agreement');
    }
  }

  const fromApi = mapRequiredStepsToWizardSteps(requiredSteps);
  if (fromApi) return fromApi;

  return (
    mapRequiredStepsToWizardSteps(getDefaultRequiredStepsForActorKind(actorKind)) || [
      WIZARD_STEPS.ENTITY_TYPE,
    ]
  );
};

export const getStepIndexInFlow = (stepItems, currentStep) =>
  Math.max(
    0,
    stepItems.findIndex((item) => item.key === currentStep),
  );

export const getChannelProfilePermissionKeysForActorKind = (actorKind) => {
  switch (actorKind) {
    case ACTOR_KINDS.OPERATOR:
      return [
        'canCreateDistributors',
        'canCreatePartners',
        'canCreateResellers',
        'canCreateCustomers',
        'canCreateReferralAgents',
      ];
    case ACTOR_KINDS.DISTRIBUTOR:
      return [
        'canCreatePartners',
        'canCreateResellers',
        'canCreateCustomers',
        'canCreateReferralAgents',
      ];
    case ACTOR_KINDS.PARTNER:
    case ACTOR_KINDS.RESELLER:
      return ['canCreateCustomers', 'canCreateReferralAgents'];
    case ACTOR_KINDS.REFERRAL_AGENT:
      return [];
    default:
      return [];
  }
};

export const CHANNEL_PROFILE_PERMISSION_DEFS = Object.freeze([
  {
    key: 'canCreateDistributors',
    labelId: 'global.text.canCreateDistributors',
    formKey: 'channelProfile.canCreateDistributors',
    summaryKey: 'summary.profileCanCreateDistributors',
  },
  {
    key: 'canCreatePartners',
    labelId: 'global.text.canCreatePartners',
    formKey: 'channelProfile.canCreatePartners',
    summaryKey: 'summary.profileCanCreatePartners',
  },
  {
    key: 'canCreateResellers',
    labelId: 'global.text.canCreateResellers',
    formKey: 'channelProfile.canCreateResellers',
    summaryKey: 'summary.profileCanCreateResellers',
  },
  {
    key: 'canCreateCustomers',
    labelId: 'global.text.canCreateCustomers',
    formKey: 'channelProfile.canCreateCustomers',
    summaryKey: 'summary.profileCanCreateCustomers',
  },
  {
    key: 'canCreateReferralAgents',
    labelId: 'global.text.canCreateReferralAgents',
    formKey: 'channelProfile.canCreateReferralAgents',
    summaryKey: 'summary.profileCanCreateReferralAgents',
  },
]);

const CHANNEL_PROFILE_PERMISSION_DEF_BY_KEY = Object.freeze(
  Object.fromEntries(CHANNEL_PROFILE_PERMISSION_DEFS.map((def) => [def.key, def])),
);

export const getChannelProfilePermissionDef = (key) =>
  CHANNEL_PROFILE_PERMISSION_DEF_BY_KEY[key] ?? null;

export const getChannelProfilePermissionDefsForActorKind = (actorKind) =>
  getChannelProfilePermissionKeysForActorKind(actorKind)
    .map(getChannelProfilePermissionDef)
    .filter(Boolean);

if (process.env.NODE_ENV === 'development') {
  Object.values(ACTOR_KINDS).forEach((actorKind) => {
    getChannelProfilePermissionKeysForActorKind(actorKind).forEach((key) => {
      if (!CHANNEL_PROFILE_PERMISSION_DEF_BY_KEY[key]) {
        // eslint-disable-next-line no-console
        console.warn(
          `[channel-onboarding] Missing CHANNEL_PROFILE_PERMISSION_DEF for key "${key}" (actorKind: ${actorKind}).`,
        );
      }
    });
  });
}

export const getActorFieldsForType = (actorKind, wizardConfig) => {
  let fields = [];

  switch (actorKind) {
    case ACTOR_KINDS.OPERATOR:
      fields = wizardConfig.operatorFields || [];
      break;
    case ACTOR_KINDS.DISTRIBUTOR:
      fields = wizardConfig.distributorFields;
      break;
    case ACTOR_KINDS.PARTNER:
      fields = wizardConfig.partnerFields;
      break;
    case ACTOR_KINDS.RESELLER:
      fields = wizardConfig.resellerFields;
      break;
    case ACTOR_KINDS.REFERRAL_AGENT:
      fields = wizardConfig.referralAgentFields;
      break;
    default:
      return [];
  }

  if (isEntityActorKind(actorKind)) {
    const profileFields = wizardConfig.getChannelProfileFields(actorKind);
    if (profileFields.length) {
      return [...fields, ...profileFields];
    }
  }

  return fields;
};

const enumToKebabApiValue = (enumObject, value) => {
  if (value == null || value === '') return undefined;
  const key = getEnumValue(enumObject, value);
  if (key) return _.kebabCase(key);
  if (typeof value === 'string') return value;
  return undefined;
};

const ACTIVE_ENTITY_STATUS = enumToKebabApiValue(
  CoreEnums.ChannelEntityStatus,
  CoreEnums.ChannelEntityStatus.Active,
);

const BILLING_SUBSCRIPTION_FIELD_KEYS = Object.freeze([
  'billingLegalName',
  'billingTaxId',
  'billingEmail',
  'billingAddressLine1',
]);

const pickBillingSubscription = (normalized) => {
  const billingSubscription = {};

  BILLING_SUBSCRIPTION_FIELD_KEYS.forEach((key) => {
    const formKey = `billingSubscription.${key}`;
    if (normalized[formKey]) {
      billingSubscription[key] = normalized[formKey];
    }
  });

  return Object.keys(billingSubscription).length ? billingSubscription : undefined;
};

const pickBillingSetup = (normalized, actorKind) => {
  if (!ACTOR_KINDS_WITH_BILLING_SETUP.has(actorKind)) return undefined;
  const billingMode = enumToKebabApiValue(
    CoreEnums.ChannelBillingMode,
    normalized['billingSetup.billingMode'],
  );
  if (!billingMode || billingMode === 'distributor-credit') return undefined;
  return { billingMode };
};

const unwrapFormEnumValue = (value) =>
  typeof value === 'object' && value != null ? value.value ?? value.id : value;

export const isEnglishUiLocale = (locale) => String(locale || '').toLowerCase().startsWith('en');

export const resolveParentActorKindApiValue = (value) => {
  const raw = unwrapFormEnumValue(value);
  if (raw == null || raw === '') return undefined;
  const kebab = enumToKebabApiValue(CoreEnums.ChannelActorKind, raw);
  if (kebab && VALID_ACTOR_KINDS.has(kebab)) return kebab;
  return typeof raw === 'string' && VALID_ACTOR_KINDS.has(raw) ? raw : undefined;
};

export const resolveParentActorCompanyId = (value) => {
  const raw = unwrapFormEnumValue(value);
  if (raw == null || raw === '') return undefined;
  return raw;
};

export const canHaveCommercialParent = (actorKind) =>
  PARENT_SELECTOR_CHILD_ACTOR_KINDS.has(resolveParentActorKindApiValue(actorKind));

export const isParentActorPairComplete = (values = {}) => {
  const hasKind = Boolean(resolveParentActorKindApiValue(values.parentActorKind));
  const hasCompany = Boolean(resolveParentActorCompanyId(values.parentActorCompanyId));
  return hasKind === hasCompany;
};

const pickParentActorPair = (normalized) => {
  const parentActorKind = resolveParentActorKindApiValue(normalized.parentActorKind);
  const parentActorCompanyId = resolveParentActorCompanyId(normalized.parentActorCompanyId);
  if (!parentActorKind || !parentActorCompanyId) return undefined;
  return { parentActorKind, parentActorCompanyId };
};

/** Billing mode chosen in the wizard, otherwise the plan default (or its only allowed mode). */
export const resolveOnboardingBillingMode = (formValues = {}, selectedPlan = null) => {
  const fromForm = parseOptionalNumericEnumFromApi(
    CoreEnums.ChannelBillingMode,
    unwrapFormEnumValue(formValues['billingSetup.billingMode']),
  );
  if (fromForm !== undefined && fromForm !== CoreEnums.ChannelBillingMode.DistributorCredit) {
    return fromForm;
  }

  const fromDefault = parseOptionalNumericEnumFromApi(
    CoreEnums.ChannelBillingMode,
    selectedPlan?.defaultBillingMode ?? selectedPlan?.DefaultBillingMode,
  );
  if (
    fromDefault !== undefined &&
    fromDefault !== CoreEnums.ChannelBillingMode.DistributorCredit
  ) {
    return fromDefault;
  }

  const allowedModes = normalizeAllowedBillingModes(
    selectedPlan?.allowedBillingModes ?? selectedPlan?.AllowedBillingModes,
  );
  if (allowedModes.length === 1) return allowedModes[0];
  return undefined;
};

export const MANUAL_INVOICE_FISCAL_FIELD_NAMES = Object.freeze([
  'billingSubscription.billingLegalName',
  'billingSubscription.billingTaxId',
  'billingSubscription.billingEmail',
]);

export const requiresManualInvoiceFiscalMinimum = (formValues, selectedPlan) =>
  resolveOnboardingBillingMode(formValues, selectedPlan) ===
  CoreEnums.ChannelBillingMode.ManualInvoice;

const pickCompany = (normalized) => {
  const company = {
    name: normalized['company.name'],
    identity: normalized['company.identity'],
    countryId: normalized['company.countryId'],
  };
  if (normalized['company.commercialName']) {
    company.commercialName = normalized['company.commercialName'];
  }
  if (normalized['company.shortCode']) {
    company.shortCode = normalized['company.shortCode'];
  }
  return company;
};

const pickOwnerUser = (normalized) => {
  const ownerUser = {
    names: normalized['ownerUser.names'],
    email: normalized['ownerUser.email'],
  };
  if (normalized['ownerUser.lastNames']) {
    ownerUser.lastNames = normalized['ownerUser.lastNames'];
  }
  return ownerUser;
};

const buildChannelProfile = (normalized, actorKind) => {
  const profile = { actorKind };

  getChannelProfilePermissionKeysForActorKind(actorKind).forEach((key) => {
    const formKey = `channelProfile.${key}`;
    if (normalized[formKey] !== undefined) {
      profile[key] = !!normalized[formKey];
    }
  });

  return profile;
};

export const buildRegisterPayload = (formValues, actorKind) => {
  const normalized = normalizeOptionValues(formValues, { skipKeys: [] });
  const agreementId = normalized.agreementId || null;

  const billingSubscription = pickBillingSubscription(normalized);

  const payload = {
    automyPlanId: normalized.automyPlanId,
    ownerUser: pickOwnerUser(normalized),
    company: pickCompany(normalized),
    channelProfile: buildChannelProfile(normalized, actorKind),
  };

  if (billingSubscription) {
    payload.billingSubscription = billingSubscription;
  }

  const billingSetup = pickBillingSetup(normalized, actorKind);
  if (billingSetup) {
    payload.billingSetup = billingSetup;
  }

  const parentActorPair = pickParentActorPair(normalized);
  if (parentActorPair) {
    payload.parentActorKind = parentActorPair.parentActorKind;
    payload.parentActorCompanyId = parentActorPair.parentActorCompanyId;
  }

  if (actorKind === ACTOR_KINDS.OPERATOR) {
    payload.operator = {
      agreementId,
      status: ACTIVE_ENTITY_STATUS,
    };
  }

  if (actorKind === ACTOR_KINDS.DISTRIBUTOR) {
    payload.distributor = {
      agreementId,
      partnerAgreementMode: enumToKebabApiValue(
        CoreEnums.DistributorPartnerAgreementMode,
        normalized['distributor.partnerAgreementMode'],
      ),
      defaultPartnerAgreementId: normalized['distributor.defaultPartnerAgreementId'] || null,
      defaultResellerAgreementId: normalized['distributor.defaultResellerAgreementId'] || null,
      defaultReferralAgentsAgreementId:
        normalized['distributor.defaultReferralAgentsAgreementId'] || null,
      status: ACTIVE_ENTITY_STATUS,
    };
  }

  if (actorKind === ACTOR_KINDS.PARTNER) {
    payload.partner = {
      agreementId,
      status: ACTIVE_ENTITY_STATUS,
    };
  }

  if (actorKind === ACTOR_KINDS.RESELLER) {
    payload.reseller = {
      agreementId,
      billingResponsibility: enumToKebabApiValue(
        CoreEnums.ResellerBillingResponsibility,
        normalized['reseller.billingResponsibility'],
      ),
      status: ACTIVE_ENTITY_STATUS,
    };
  }

  if (actorKind === ACTOR_KINDS.REFERRAL_AGENT) {
    payload.referralAgent = {
      agreementId,
      status: ACTIVE_ENTITY_STATUS,
    };
  }

  return payload;
};

export const getStepFieldNames = (step, actorKind, context = {}) => {
  switch (step) {
    case WIZARD_STEPS.PLAN:
      return ['automyPlanId'];
    case WIZARD_STEPS.COMPANY:
      return [
        'company.name',
        'company.identity',
        'company.countryId',
        'ownerUser.names',
        'ownerUser.email',
      ];
    case WIZARD_STEPS.ACTOR: {
      const names = [];
      if (actorKind === ACTOR_KINDS.DISTRIBUTOR) {
        names.push('distributor.partnerAgreementMode');
      }
      if (actorKind === ACTOR_KINDS.RESELLER) {
        names.push('reseller.billingResponsibility');
      }
      if (resolveParentActorKindApiValue(context.formValues?.parentActorKind)) {
        names.push('parentActorCompanyId');
      }
      return names;
    }
    case WIZARD_STEPS.AGREEMENT:
      return ['agreementId'];
    case WIZARD_STEPS.BILLING:
      return requiresManualInvoiceFiscalMinimum(context.formValues, context.selectedPlan)
        ? [...MANUAL_INVOICE_FISCAL_FIELD_NAMES]
        : [];
    default:
      return [];
  }
};

export const isWizardStepReady = ({
  currentStep,
  actorKind,
  formConfig,
  form,
  formValues = {},
  registrationOptionsLoading = false,
}) => {
  if (currentStep === WIZARD_STEPS.ENTITY_TYPE) {
    if (registrationOptionsLoading) return false;
    return Boolean(actorKind);
  }

  if (
    currentStep === WIZARD_STEPS.SUMMARY ||
    (currentStep === WIZARD_STEPS.ACTOR && !formConfig?.fields?.length)
  ) {
    return true;
  }

  if (!formConfig?.fields?.length) {
    return false;
  }

  const values = { ...formValues, ...form?.getFieldsValue?.(true) };

  if (!checkFormState(formConfig, form, values)) {
    return false;
  }

  if (currentStep === WIZARD_STEPS.ACTOR && !isParentActorPairComplete(values)) {
    return false;
  }

  return true;
};

export const mapOnboardingValidationFieldToFormName = (fieldPath = '') => {
  if (!fieldPath) return fieldPath;

  return String(fieldPath)
    .split('.')
    .map((segment) => (segment ? _.camelCase(segment) : segment))
    .join('.');
};

const PARENT_ACTOR_FORM_FIELDS = ['parentActorKind', 'parentActorCompanyId'];

const getValidationErrorSearchText = (item = {}) =>
  [item.field, item.errorCode, item.code]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export const isParentActorValidationError = (item = {}) =>
  getValidationErrorSearchText(item).includes('parentactor');

export const mapOnboardingValidationErrorsForForm = (validationErrors = []) =>
  validationErrors.flatMap((item) => {
    const mappedField = mapOnboardingValidationFieldToFormName(item.field);
    if (isParentActorValidationError(item) && !mappedField) {
      return PARENT_ACTOR_FORM_FIELDS.map((field) => ({ ...item, field }));
    }
    return [{ ...item, field: mappedField }];
  });

export const mapValidationErrorToStep = (fieldPath = '', actorKind = null, errorItem = null) => {
  if (errorItem && isParentActorValidationError(errorItem)) return WIZARD_STEPS.ACTOR;
  const path = String(fieldPath).toLowerCase();
  if (path.includes('parentactor')) return WIZARD_STEPS.ACTOR;
  if (path.includes('automyplan')) return WIZARD_STEPS.PLAN;
  if (path.includes('owneruser')) return WIZARD_STEPS.COMPANY;
  if (path.includes('company')) return WIZARD_STEPS.COMPANY;
  if (path.includes('agreement')) return WIZARD_STEPS.AGREEMENT;
  if (path.includes('actorkind')) return WIZARD_STEPS.ENTITY_TYPE;
  if (path.includes('channelprofile')) {
    return isEntityActorKind(actorKind) ? WIZARD_STEPS.ACTOR : WIZARD_STEPS.COMPANY;
  }
  if (path.includes('billingsubscription')) return WIZARD_STEPS.BILLING;
  if (path.includes('billingsetup') || path.includes('billing-setup')) return WIZARD_STEPS.PLAN;
  if (
    path.includes('distributor') ||
    path.includes('partner') ||
    path.includes('reseller') ||
    path.includes('referralagent') ||
    path.includes('operator')
  ) {
    return WIZARD_STEPS.ACTOR;
  }
  return WIZARD_STEPS.SUMMARY;
};
